import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { useReducedMotion } from '../../../../hooks/useReducedMotion';
import { SECURE_BRAND_ANIM } from '../../../../theme/tokens';
import { DeclarationPicture } from '../DeclarationPicture';
import { EmailPicture } from '../EmailPicture';
import { SigningPicture } from '../SigningPicture';
import {
  Action,
  Body,
  Card,
  Cards,
  Head,
  Logo,
  Note,
  PoweredBy,
  PoweredByLabel,
  Root,
  Stage,
  StageInner,
  StepBadge,
  StepLine,
  StepText,
  Title,
} from './StepCards.styles';

export type StepCardsProps = {
  /**
   * Document sent: step 1 is done (`again` after a Resend in this visit) and signing is the owner's to
   * do, so it is lit while the other two dim back. `sends`: the sends that landed in this visit, a count
   * that only grows (each new one replays step 1's sent beat). Null on Not secured.
   */
  sent: { again: boolean; sends: number } | null;
  /** Step 1's controls: Start (Not secured), or Resend and Start over (Document sent), and under them
   *  why they are disabled when they are. */
  action: React.ReactNode;
};

const {
  CARD_DELAYS_MS,
  STAGE_DELAY_MS,
  SEAL_DELAY_MS,
  SEAL_MS,
  SEAL_RING_MS,
  EMAIL_DELAY_MS,
  TAP_DELAY_MS,
  TAP_MS,
  SIGN_DELAY_MS,
  PEN_IN_MS,
  SIGN_DRAW_MS,
  PEN_OUT_MS,
  CHECK_MS,
  SPOTLIGHT_LEAD_MS,
  SIGN_LOOP_REST_MS,
} = SECURE_BRAND_ANIM;

/** When each picture's beat starts on the entrance: its card's turn, the picture's, then its own lead. */
const FIRST_BEAT_MS = [SEAL_DELAY_MS, EMAIL_DELAY_MS, SIGN_DELAY_MS].map(
  (lead, index) => CARD_DELAYS_MS[index] + STAGE_DELAY_MS + lead,
);
/** How long each picture's beat runs; a hover replays a picture only once its beat is over. */
const BEAT_MS = [SEAL_MS * 0.5 + SEAL_RING_MS, TAP_DELAY_MS + TAP_MS, PEN_IN_MS + SIGN_DRAW_MS + PEN_OUT_MS + CHECK_MS];
/** A replay starts almost at once: the hover is the cue. */
const REPLAY_LEAD_MS = 80;
/** The signing step's index. */
const SIGN = 2;

/**
 * How it works, on the Not secured and Document sent layouts: the License Holder Declaration's three
 * steps as cards side by side (stacked when the row is narrow), each over a picture of the step. Step 1
 * holds the step's controls (`action`). Step 2 is the DocuSign email: check the inbox, open it, Review
 * Document, with the note for DocuSign's recognition check. Step 3 is signing. DocuSign's "Powered by"
 * badge sits on the heading's row.
 *
 * Document sent (`sent`) is the confirmation of a send: step 1 turns done ("Contract sent", a check for
 * its number; a send landing on screen stamps its seal and pops its check again), steps 1 and 2 dim
 * back (step 1's buttons stay as they are), and step 3 is the spotlight: lifted, lit in the brand
 * orange, its number filled, `aria-current="step"`, and its signature written again and again. The
 * section keeps these cards mounted from Not secured to Document sent, so when a send lands they turn
 * in place.
 *
 * Motion: as the cards mount (the status has loaded and this layout shows), they rise into place one
 * after another, each picture following its card and then playing its beat once. Hovering a card plays
 * its picture's beat again, once the last one has finished. Reduced motion: none of it (no entrance, no
 * loop), each picture at its last frame; the spotlight still lifts and lights step 3, without moving.
 */
export const StepCards = ({ sent, action }: StepCardsProps): React.ReactElement => {
  const { t } = useTranslation();
  const still = useReducedMotion();
  // Each picture's play count: a new count remounts the picture, which plays its beat again.
  const [plays, setPlays] = React.useState<readonly number[]>([0, 0, 0]);
  // Until when each picture is busy with its beat (performance.now() time), set when it starts one.
  const busyUntil = React.useRef<number[] | null>(null);

  // The entrance's beats start on mount: a hover replays a picture only once its first beat is over.
  React.useEffect(() => {
    const now = performance.now();
    busyUntil.current = FIRST_BEAT_MS.map((first, index) => now + first + BEAT_MS[index]);
  }, []);

  /** Plays a picture's beat again now: it is busy until the beat ends. */
  const play = React.useCallback((index: number): void => {
    const busy = busyUntil.current;
    if (!busy) return;
    busy[index] = performance.now() + REPLAY_LEAD_MS + BEAT_MS[index];
    setPlays((current) => current.map((count, i) => (i === index ? count + 1 : count)));
  }, []);

  /** A hover's replay: only once the picture's last beat is over. */
  const replay = (index: number): void => {
    const busy = busyUntil.current;
    if (still || !busy || performance.now() < busy[index]) return;
    play(index);
  };

  // The lit signing step writes its signature again and again (a timer, rescheduled after every play):
  // soon after it is lit, else a rest after the signature on screen is done.
  const spotlight = sent !== null;
  const signPlays = plays[SIGN];

  // A send landing while the cards are on screen (Start, Start over or Resend, each time) stamps step 1's
  // seal again, and its check pops in again (keyed below): the visible beat that says it was sent.
  const sends = sent === null ? 0 : sent.sends;
  const shownSends = React.useRef(sends);
  React.useEffect(() => {
    if (sends > shownSends.current && !still) play(0);
    shownSends.current = sends;
  }, [sends, still, play]);
  React.useEffect(() => {
    if (!spotlight || still) return undefined;
    const busy = busyUntil.current;
    const left = busy ? busy[SIGN] - performance.now() : 0;
    const wait = left > 0 ? left + SIGN_LOOP_REST_MS : SPOTLIGHT_LEAD_MS;
    const timer = window.setTimeout(() => play(SIGN), wait);
    return () => window.clearTimeout(timer);
  }, [spotlight, still, signPlays, play]);

  /** A picture's beat: on the entrance after its card and picture arrive, on a replay almost at once. */
  const beatAt = (index: number): number => (plays[index] === 0 ? FIRST_BEAT_MS[index] : REPLAY_LEAD_MS);

  return (
    <Root>
      <Head>
        <Title text="How it works" tag="h3" />
        <PoweredBy>
          <PoweredByLabel text="Powered by" />
          <Logo label={t('DocuSign')} />
        </PoweredBy>
      </Head>
      <Cards>
        <Card $delayMs={CARD_DELAYS_MS[0]} data-emphasis={spotlight ? 'dim' : 'none'} onPointerEnter={() => replay(0)}>
          <Stage $tone="start">
            <StageInner $delayMs={CARD_DELAYS_MS[0] + STAGE_DELAY_MS}>
              <DeclarationPicture key={plays[0]} delayMs={beatAt(0)} />
            </StageInner>
          </Stage>
          <Body>
            <StepLine>
              {sent ? (
                <>
                  <StepBadge key={sends} $state="done">
                    <Icon name="check" size={14} />
                  </StepBadge>
                  {sent.again ? <StepText text="Contract sent again" /> : <StepText text="Contract sent" />}
                </>
              ) : (
                <>
                  <StepBadge $state="todo" />
                  <StepText text="Click Start below." />
                </>
              )}
            </StepLine>
            <Action>{action}</Action>
          </Body>
        </Card>
        <Card $delayMs={CARD_DELAYS_MS[1]} data-emphasis={spotlight ? 'dim' : 'none'} onPointerEnter={() => replay(1)}>
          <Stage $tone="email">
            <StageInner $delayMs={CARD_DELAYS_MS[1] + STAGE_DELAY_MS}>
              <EmailPicture key={plays[1]} delayMs={beatAt(1)} />
            </StageInner>
          </Stage>
          <Body>
            <StepLine>
              <StepBadge $state="todo" />
              <StepText text="Open the email from DocuSign in your inbox and click Review Document." />
            </StepLine>
            <Note
              text="If DocuSign first asks whether you recognize this document, continue to open it."
              tag="paragraph"
            />
          </Body>
        </Card>
        <Card
          $delayMs={CARD_DELAYS_MS[2]}
          data-emphasis={spotlight ? 'spotlight' : 'none'}
          aria-current={spotlight ? 'step' : undefined}
          onPointerEnter={() => replay(SIGN)}
        >
          <Stage $tone="sign">
            <StageInner $delayMs={CARD_DELAYS_MS[2] + STAGE_DELAY_MS}>
              <SigningPicture key={plays[SIGN]} delayMs={beatAt(SIGN)} still={still} />
            </StageInner>
          </Stage>
          <Body>
            <StepLine>
              <StepBadge $state={spotlight ? 'current' : 'todo'} />
              <StepText text="Sign the document." />
            </StepLine>
          </Body>
        </Card>
      </Cards>
    </Root>
  );
};

export default StepCards;
